import type { AuthTokenResponse, LoginResponse } from '@/features/auth/types'
import type { User } from '@/shared/types'
import { bareHttp, http } from '@/core/api'
import { AUTH_ENDPOINTS } from '@/features/auth/api/auth.endpoints'

type AuthClient = 'crm' | 'recruitment_portal'

function authClientPayload(authClient?: AuthClient): { auth_client?: AuthClient } {
  return authClient && authClient !== 'crm' ? { auth_client: authClient } : {}
}

function authClientRequestHeaders(authClient?: AuthClient): Record<string, string> | undefined {
  if (!authClient || authClient === 'crm') return undefined
  return { 'X-Auth-Client': authClient }
}

function portalAccessPayload(access?: string | null): { access?: string } {
  const token = access?.trim() ?? ''
  return token ? { access: token } : {}
}

function portalAccessHeaders(
  authClient?: AuthClient,
  access?: string | null,
): Record<string, string> | undefined {
  const headers = { ...authClientRequestHeaders(authClient) }
  const token = access?.trim() ?? ''
  if (token) {
    headers['X-Portal-Access'] = token
  }
  return Object.keys(headers).length ? headers : undefined
}

export const authApi = {
  async login(
    login: string,
    password: string,
    remember = true,
    authClient: AuthClient = 'crm',
    access?: string | null,
  ): Promise<LoginResponse> {
    const { data } = await bareHttp.post<LoginResponse>(
      AUTH_ENDPOINTS.LOGIN,
      {
        login: login.trim(),
        password,
        remember,
        ...authClientPayload(authClient),
        ...portalAccessPayload(access),
      },
      { headers: portalAccessHeaders(authClient, access) },
    )
    return data
  },

  async verifyTwoFactor(
    challengeId: string,
    code: string,
    authClient: AuthClient = 'crm',
    access?: string | null,
  ): Promise<AuthTokenResponse> {
    const { data } = await bareHttp.post<AuthTokenResponse>(
      AUTH_ENDPOINTS.TWO_FACTOR,
      {
        challenge_id: challengeId,
        code,
        ...authClientPayload(authClient),
        ...portalAccessPayload(access),
      },
      { headers: portalAccessHeaders(authClient, access) },
    )
    return data
  },

  async refresh(authClient: AuthClient = 'crm', access?: string | null): Promise<AuthTokenResponse> {
    const { data } = await bareHttp.post<AuthTokenResponse>(
      AUTH_ENDPOINTS.REFRESH,
      {
        ...authClientPayload(authClient),
        ...portalAccessPayload(access),
      },
      { headers: portalAccessHeaders(authClient, access) },
    )
    return data
  },

  async me(): Promise<{ user: User }> {
    const { data } = await http.get(AUTH_ENDPOINTS.ME)
    return data
  },

  async logout(): Promise<void> {
    await http.post(AUTH_ENDPOINTS.LOGOUT)
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const { data } = await bareHttp.post<{ message: string }>(AUTH_ENDPOINTS.FORGOT_PASSWORD, {
      email,
    })
    return data
  },

  async resetPassword(payload: {
    email: string
    token: string
    password: string
    password_confirmation: string
  }): Promise<{ message: string }> {
    const { data } = await bareHttp.post<{ message: string }>(AUTH_ENDPOINTS.RESET_PASSWORD, payload)
    return data
  },

  async setupPassword(payload: {
    email: string
    token: string
    password: string
    password_confirmation: string
  }): Promise<{ message: string }> {
    const { data } = await bareHttp.post<{ message: string }>(AUTH_ENDPOINTS.SETUP_PASSWORD, payload)
    return data
  },
}
